/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import './AdapterBenchmark.css';

const AdapterBenchmark = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  // State
  const [adapters, setAdapters] = useState([]);
  const [selectedAdapter, setSelectedAdapter] = useState('');
  const [selectedTestSuite, setSelectedTestSuite] = useState('comprehensive');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [currentBenchmark, setCurrentBenchmark] = useState(null);
  const [benchmarkHistory, setBenchmarkHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState(0);

  // Test suite configurations
  const testSuites = {
    quick: {
      name: 'Quick Test',
      description: '5-10 basic tests per category (~2 minutes)',
      testCount: 'Low'
    },
    comprehensive: {
      name: 'Comprehensive Test',
      description: '20-30 tests per category (~10 minutes)',
      testCount: 'Medium'
    },
    extensive: {
      name: 'Extensive Test',
      description: '50+ tests per category (~30 minutes)',
      testCount: 'High'
    }
  };

  /**
   * Load adapters on mount
   */
  useEffect(() => {
    loadAdapters();
  }, []);

  /**
   * Load benchmark history when adapter selected
   */
  useEffect(() => {
    if (selectedAdapter) {
      loadBenchmarkHistory(selectedAdapter);
    }
  }, [selectedAdapter]);

  const loadAdapters = async () => {
    try {
      setLoading(true);
      setError('');

      const data = await adminApi.getAdapters({ active: true });
      setAdapters(data.adapters || []);

      // Auto-select first adapter
      if (data.adapters && data.adapters.length > 0) {
        setSelectedAdapter(data.adapters[0]._id);
      }

    } catch (err) {
      console.error('Load adapters error:', err);
      setError(err.response?.data?.message || 'Failed to load adapters');
    } finally {
      setLoading(false);
    }
  };

  const loadBenchmarkHistory = async (adapterId) => {
    try {
      const data = await adminApi.getAdapterBenchmarkHistory(adapterId, 10);
      setBenchmarkHistory(data.benchmarks || []);
    } catch (err) {
      console.error('Load benchmark history error:', err);
      // Don't show error if no history found
      setBenchmarkHistory([]);
    }
  };

  /**
   * Run benchmark
   */
  const handleRunBenchmark = async () => {
    if (!selectedAdapter) {
      alert('Please select an adapter to benchmark');
      return;
    }

    if (!hasPermission('adapter.test')) {
      alert('You do not have permission to run adapter benchmarks');
      return;
    }

    try {
      setRunning(true);
      setError('');
      setProgress(0);
      setShowResults(false);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return prev + 5;
        });
      }, 1000);

      // Run benchmark
      const config = {
        testSuiteType: selectedTestSuite,
        includePerformanceMetrics: true,
        timeout: 300000 // 5 minutes
      };

      const result = await adminApi.runBenchmark(selectedAdapter, selectedTestSuite, config);

      clearInterval(progressInterval);
      setProgress(100);

      setCurrentBenchmark(result.benchmark);
      setShowResults(true);

      // Reload history
      loadBenchmarkHistory(selectedAdapter);

    } catch (err) {
      console.error('Run benchmark error:', err);
      setError(err.response?.data?.message || 'Failed to run benchmark');
    } finally {
      setRunning(false);
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Get score color class
   */
  const getScoreColor = (score) => {
    if (score >= 90) return 'score-excellent';
    if (score >= 75) return 'score-good';
    if (score >= 60) return 'score-fair';
    return 'score-poor';
  };

  /**
   * Get status badge class
   */
  const getStatusBadge = (passed) => {
    return passed ? 'badge-pass' : 'badge-fail';
  };

  /**
   * Safely format number with toFixed
   */
  const safeToFixed = (value, decimals = 1, defaultValue = '--') => {
    if (value === null || value === undefined || isNaN(value)) {
      return defaultValue;
    }
    return Number(value).toFixed(decimals);
  };

  /**
   * View benchmark details
   */
  const viewBenchmarkDetails = (benchmark) => {
    setCurrentBenchmark(benchmark);
    setShowResults(true);
  };

  // Check permission
  if (!hasPermission('adapter.test')) {
    return (
      <AdminLayout showBackButton backPath="/admin/dashboard">
        <div className="benchmark-container">
          <div className="error-message">
            ❌ You do not have permission to access adapter benchmarks.
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div className="benchmark-container">

        {/* Header */}
        <div className="benchmark-header">
          <div>
            <h1 className="benchmark-title">🎯 Adapter Benchmark</h1>
            <p className="benchmark-subtitle">Test adapter quality and performance with automated test suites</p>
          </div>
        </div>

        {error && (
          <div className="error-message">
            ❌ {error}
          </div>
        )}

        {/* Configuration Section */}
        <div className="benchmark-config-section">
          <h2 className="section-title">Configure Benchmark</h2>

          <div className="config-grid">
            {/* Adapter Selection */}
            <div className="config-item">
              <label className="config-label">Select Adapter</label>
              <select
                className="config-select"
                value={selectedAdapter}
                onChange={(e) => setSelectedAdapter(e.target.value)}
                disabled={running || loading}
              >
                <option value="">-- Select Adapter --</option>
                {adapters.map(adapter => (
                  <option key={adapter._id} value={adapter._id}>
                    {adapter.domain} - v{adapter.version} ({adapter.adapterName})
                  </option>
                ))}
              </select>
            </div>

            {/* Test Suite Selection */}
            <div className="config-item">
              <label className="config-label">Test Suite</label>
              <select
                className="config-select"
                value={selectedTestSuite}
                onChange={(e) => setSelectedTestSuite(e.target.value)}
                disabled={running}
              >
                {Object.entries(testSuites).map(([key, suite]) => (
                  <option key={key} value={key}>
                    {suite.name} - {suite.testCount} tests
                  </option>
                ))}
              </select>
              <p className="config-description">
                {testSuites[selectedTestSuite]?.description}
              </p>
            </div>
          </div>

          {/* Run Button */}
          <div className="run-section">
            <button
              className="btn-run-benchmark"
              onClick={handleRunBenchmark}
              disabled={!selectedAdapter || running || loading}
            >
              {running ? '⏳ Running Benchmark...' : '▶️ Run Benchmark'}
            </button>
          </div>

          {/* Progress Bar */}
          {running && (
            <div className="progress-section">
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${progress}%` }}>
                  {progress}%
                </div>
              </div>
              <p className="progress-text">
                Running tests across all categories... This may take several minutes.
              </p>
            </div>
          )}
        </div>

        {/* Results Section */}
        {showResults && currentBenchmark && (
          <div className="benchmark-results-section">
            <div className="results-header">
              <h2 className="section-title">📊 Benchmark Results</h2>
              <span className={`status-badge ${getStatusBadge(currentBenchmark.passed)}`}>
                {currentBenchmark.passed ? '✅ PASSED' : '❌ FAILED'}
              </span>
            </div>

            {/* Overall Score */}
            <div className="overall-score-card">
              <div className="score-content">
                <div className="score-label">Overall Score</div>
                <div className={`score-value ${getScoreColor(currentBenchmark.overallScore || 0)}`}>
                  {safeToFixed(currentBenchmark.overallScore, 1)}%
                </div>
              </div>
              <div className="score-meta">
                <div className="meta-item">
                  <span className="meta-label">Executed:</span>
                  <span className="meta-value">{formatDate(currentBenchmark.executedAt)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Duration:</span>
                  <span className="meta-value">{safeToFixed(currentBenchmark.executionTime / 1000, 1)}s</span>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            {currentBenchmark.performance && (
              <div className="performance-card">
                <h3 className="card-title">⚡ Performance Metrics</h3>
                <div className="metrics-grid">
                  <div className="metric-item">
                    <div className="metric-label">Avg Latency</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.performance.avgLatency, 0)}ms</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-label">P50 Latency</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.performance.p50Latency, 0)}ms</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-label">P95 Latency</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.performance.p95Latency, 0)}ms</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-label">P99 Latency</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.performance.p99Latency, 0)}ms</div>
                  </div>
                </div>
              </div>
            )}

            {/* Quality Metrics */}
            {currentBenchmark.quality && (
              <div className="quality-card">
                <h3 className="card-title">✨ Quality Metrics</h3>
                <div className="metrics-grid">
                  <div className="metric-item">
                    <div className="metric-label">Accuracy</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.quality.accuracy * 100, 1)}%</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-label">Precision</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.quality.precision * 100, 1)}%</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-label">Recall</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.quality.recall * 100, 1)}%</div>
                  </div>
                  <div className="metric-item">
                    <div className="metric-label">F1 Score</div>
                    <div className="metric-value">{safeToFixed(currentBenchmark.quality.f1Score * 100, 1)}%</div>
                  </div>
                </div>
              </div>
            )}

            {/* Category Results */}
            <div className="categories-section">
              <h3 className="section-title">📋 Test Categories</h3>
              <div className="categories-grid">
                {currentBenchmark.categories && currentBenchmark.categories.map((category, idx) => (
                  <div key={idx} className="category-card">
                    <div className="category-header">
                      <h4 className="category-name">{category.name}</h4>
                      <span className={`score-badge ${getScoreColor(category.score || 0)}`}>
                        {safeToFixed(category.score, 1)}%
                      </span>
                    </div>
                    <div className="category-stats">
                      <span className="stat-item">
                        ✅ {category.testsPassed || 0} / {category.testsTotal || 0} passed
                      </span>
                      <span className="stat-item">
                        ⚖️ Weight: {category.weight || 1}x
                      </span>
                    </div>

                    {/* Individual Tests */}
                    {category.tests && category.tests.length > 0 && (
                      <div className="tests-list">
                        {category.tests.slice(0, 3).map((test, testIdx) => (
                          <div key={testIdx} className={`test-item ${test.passed ? 'test-pass' : 'test-fail'}`}>
                            <span className="test-icon">{test.passed ? '✅' : '❌'}</span>
                            <span className="test-name">{test.name}</span>
                            <span className="test-score">{safeToFixed(test.score, 0)}%</span>
                          </div>
                        ))}
                        {category.tests.length > 3 && (
                          <div className="test-more">
                            + {category.tests.length - 3} more tests
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Benchmark History */}
        {benchmarkHistory.length > 0 && (
          <div className="history-section">
            <h2 className="section-title">📜 Recent Benchmark History</h2>
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Executed</th>
                    <th>Overall Score</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarkHistory.map((benchmark, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(benchmark.executedAt)}</td>
                      <td>
                        <span className={`score-badge ${getScoreColor(benchmark.overallScore || 0)}`}>
                          {safeToFixed(benchmark.overallScore, 1)}%
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusBadge(benchmark.passed)}`}>
                          {benchmark.passed ? 'PASSED' : 'FAILED'}
                        </span>
                      </td>
                      <td>{safeToFixed(benchmark.executionTime / 1000, 1)}s</td>
                      <td>
                        <button
                          className="btn-view-details"
                          onClick={() => viewBenchmarkDetails(benchmark)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && adapters.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Active Adapters Found</h3>
            <p>Please ensure you have active adapters before running benchmarks.</p>
            <button className="btn-secondary" onClick={() => navigate('/admin/adapters')}>
              Go to Adapter Management
            </button>
          </div>
        )}

      </div>
    </AdminLayout>
  );
};

export default AdapterBenchmark;
